<script>
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxHealthAPI from 'dashboard/api/inboxHealth';

export default {
  components: { NextButton },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      pixelId: '',
      accessToken: '',
      testEventCode: '',
      pageId: '',
      isSaving: false,
      isDispatching: false,
    };
  },
  computed: {
    canDispatchTest() {
      return !!this.pixelId && !!this.accessToken && !this.isDispatching;
    },
    isConfigured() {
      return !!this.pixelId && !!this.accessToken;
    },
  },
  watch: {
    inbox: {
      immediate: true,
      handler() {
        this.loadFromInbox();
      },
    },
  },
  methods: {
    loadFromInbox() {
      const cfg = this.inbox.provider_config || {};
      this.pixelId = cfg.meta_pixel_id || '';
      this.accessToken = cfg.meta_capi_access_token || '';
      this.testEventCode = cfg.meta_capi_test_event_code || '';
      this.pageId = cfg.meta_capi_page_id || '';
    },
    async save() {
      this.isSaving = true;
      try {
        const newConfig = {
          ...(this.inbox.provider_config || {}),
          meta_pixel_id: this.pixelId.trim(),
          meta_capi_access_token: this.accessToken.trim(),
          meta_capi_test_event_code: this.testEventCode.trim(),
          meta_capi_page_id: this.pageId.trim(),
        };
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: { provider_config: newConfig },
        });
        useAlert(this.$t('INBOX_MGMT.META_PIXEL.SAVE_SUCCESS'));
      } catch (e) {
        useAlert(this.$t('INBOX_MGMT.META_PIXEL.SAVE_ERROR'));
      } finally {
        this.isSaving = false;
      }
    },
    async dispatchTest() {
      if (!this.canDispatchTest) {
        useAlert(this.$t('INBOX_MGMT.META_PIXEL.TEST_NEEDS_PIXEL'));
        return;
      }
      this.isDispatching = true;
      try {
        const { data } = await InboxHealthAPI.dispatchMetaCapiTestEvent(
          this.inbox.id
        );
        if (data.ok) {
          useAlert(
            this.$t('INBOX_MGMT.META_PIXEL.TEST_SUCCESS', {
              count: data.events_received,
            })
          );
        } else {
          useAlert(
            this.$t('INBOX_MGMT.META_PIXEL.TEST_ERROR', {
              message: data.error || 'unknown',
            })
          );
        }
      } catch (e) {
        useAlert(
          this.$t('INBOX_MGMT.META_PIXEL.TEST_ERROR', {
            message: e?.response?.data?.error || e.message || 'unknown',
          })
        );
      } finally {
        this.isDispatching = false;
      }
    },
  },
};
</script>

<template>
  <div class="mx-6 max-w-3xl">
    <div class="mb-6">
      <h2 class="text-heading-2 text-n-slate-12 mb-1">
        {{ $t('INBOX_MGMT.META_PIXEL.TITLE') }}
      </h2>
      <p class="text-n-slate-11">
        {{ $t('INBOX_MGMT.META_PIXEL.DESCRIPTION') }}
      </p>
      <div
        v-if="isConfigured"
        class="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded bg-n-teal-3 text-n-teal-12 text-sm"
      >
        ✓ Connected
      </div>
    </div>

    <form class="flex flex-col gap-5" @submit.prevent="save()">
      <div>
        <label class="text-sm font-medium text-n-slate-12 mb-1 block">
          {{ $t('INBOX_MGMT.META_PIXEL.PIXEL_ID_LABEL') }}
        </label>
        <woot-input
          v-model="pixelId"
          class="[&>input]:!mb-0"
          :placeholder="$t('INBOX_MGMT.META_PIXEL.PIXEL_ID_PLACEHOLDER')"
        />
        <p class="text-xs text-n-slate-10 mt-1">
          {{ $t('INBOX_MGMT.META_PIXEL.PIXEL_ID_HINT') }}
        </p>
      </div>

      <div>
        <label class="text-sm font-medium text-n-slate-12 mb-1 block">
          {{ $t('INBOX_MGMT.META_PIXEL.ACCESS_TOKEN_LABEL') }}
        </label>
        <woot-input
          v-model="accessToken"
          type="password"
          class="[&>input]:!mb-0"
          :placeholder="$t('INBOX_MGMT.META_PIXEL.ACCESS_TOKEN_PLACEHOLDER')"
        />
        <p class="text-xs text-n-slate-10 mt-1">
          {{ $t('INBOX_MGMT.META_PIXEL.ACCESS_TOKEN_HINT') }}
        </p>
      </div>

      <div>
        <label class="text-sm font-medium text-n-slate-12 mb-1 block">
          {{ $t('INBOX_MGMT.META_PIXEL.PAGE_ID_LABEL') }}
        </label>
        <woot-input
          v-model="pageId"
          class="[&>input]:!mb-0"
          :placeholder="$t('INBOX_MGMT.META_PIXEL.PAGE_ID_PLACEHOLDER')"
        />
        <p class="text-xs text-n-slate-10 mt-1">
          {{ $t('INBOX_MGMT.META_PIXEL.PAGE_ID_HINT') }}
        </p>
      </div>

      <div>
        <label class="text-sm font-medium text-n-slate-12 mb-1 block">
          {{ $t('INBOX_MGMT.META_PIXEL.TEST_EVENT_CODE_LABEL') }}
        </label>
        <woot-input
          v-model="testEventCode"
          class="[&>input]:!mb-0"
          :placeholder="$t('INBOX_MGMT.META_PIXEL.TEST_EVENT_CODE_PLACEHOLDER')"
        />
        <p class="text-xs text-n-slate-10 mt-1">
          {{ $t('INBOX_MGMT.META_PIXEL.TEST_EVENT_CODE_HINT') }}
        </p>
      </div>

      <div class="flex gap-3 mt-2">
        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.META_PIXEL.SAVE')"
          :is-loading="isSaving"
          :disabled="isSaving"
        />
        <NextButton
          type="button"
          variant="outline"
          :label="$t('INBOX_MGMT.META_PIXEL.DISPATCH_TEST')"
          :is-loading="isDispatching"
          :disabled="!canDispatchTest"
          @click="dispatchTest()"
        />
      </div>
    </form>
  </div>
</template>
